<script setup lang="ts">
import { ref, watch } from "vue";
import type { PromotionCode } from "@/rvapi";
import { inXDays, local2utc, utc2local } from "@/utils";
import { usePromotionStore } from "@/stores/promotions";

const promotions = usePromotionStore();
const codecount = ref("1");
const codeexpire = ref(inXDays(365 * 2));
const codevalue = ref("1");
const resultcodes = ref([] as PromotionCode[]);
const campaign = ref("");
const campaignError = ref("");
const countError = ref("");
const state = ref("create");
const close = () => {
  state.value = "create";
};
const isDateInvalid = ref(false);
const dateErrorMessage = ref("");

watch(codecount, (data) => {
  if (!data || !/^\d*$/.test(data)) {
    countError.value = "Must be a number";
    return;
  }
  const val = parseInt(data, 10);
  if (val === 0) {
    countError.value = "Must be a number";
    return;
  }
  if (val > 2000) {
    countError.value = "Maximum batch size is 2000 codes";
    return;
  }
  countError.value = "";
});
watch(campaign, () => {
  campaignError.value = "";
});

const emit = defineEmits<{
  (e: "codeCreationFailed"): void;
}>();

const getCampaignLabel = () => {
  const l = campaign.value;
  if (!l) return undefined;
  if (/^[a-zA-Z0-9]+$/.test(l)) return l.toLowerCase();
  campaignError.value = "Invalid text label. Alphanumeric only please.";
  state.value = "create";

  throw "invalid campaign label";
};
const create = async () => {
  try {
    if (!codeexpire.value || isDateInvalid.value) {
      return;
    }
    state.value = "uploading";
    const campaignlabel = getCampaignLabel();
    const codes = await promotions.createUnique(
      parseInt(codecount.value, 10),
      parseInt(codevalue.value, 10),
      local2utc(codeexpire.value, "23:59:00"),
      campaignlabel,
    );
    codecount.value = "1";
    codeexpire.value = inXDays(365 * 2);
    codevalue.value = "1";
    resultcodes.value = codes.codes;
    campaign.value = "";
    state.value = "result";
  } catch {
    console.log("Error in creating unique code");
    state.value = "create";
    emit("codeCreationFailed");
  }
};

const dateSelected = (date: { detail: { start: string } }) => {
  codeexpire.value = date.detail?.start;
  dateErrorMessage.value = "";
  isDateInvalid.value = false;
};

const invalidDateEntered = (date: { detail: { start: string } }) => {
  codeexpire.value = date.detail?.start;
  dateErrorMessage.value = "Invalid date Entered";
  isDateInvalid.value = true;
};
</script>

<template>
  <h2>Create unique codes</h2>
  <pn-spinner size="2" v-show="state == 'uploading'" />
  <div v-show="state == 'create'">
    <p>
      <pn-input
        v-model="codecount"
        label="Batch size"
        helpertext="Number of random codes to generate"
        :error="countError"
      />
    </p>
    <p>
      <pn-input
        v-model="codevalue"
        label="Free cards"
        helpertext="Number of free cards each code will give."
      />
    </p>
    <p>
      <pn-input
        v-model="campaign"
        label="Batch label"
        helpertext="A name to help identify this batch of codes in later metrics"
        :error="campaignError"
      />
    </p>

    <p class="date-picker">
      <pn-date-picker
        label-from="Expire date"
        :start="codeexpire"
        :invalid="isDateInvalid"
        @dateSelection="dateSelected"
        @dateInvalid="invalidDateEntered"
        :error="dateErrorMessage"
        class="datePicker"
      />
    </p>
    <p v-show="!countError">
      <pn-button v-on:click="create">Create</pn-button>
    </p>
  </div>

  <div v-show="state == 'result'">
    <h3>Result</h3>
    <pn-table bordered="true" striped="true">
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Valid until</th>
            <th>Created by</th>
            <th>Created at</th>
            <th>Cards per purchase</th>
            <th>Usage limit</th>
            <th>Require Login</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="code in resultcodes" :key="code.code">
            <td>
              {{ code.code }}
            </td>
            <td>
              {{ utc2local(code.expireAt) }}
            </td>
            <td>
              {{ code.createdBy }}
            </td>
            <td>
              {{ utc2local(code.createdAt) }}
            </td>
            <td>
              {{ code.value }}
            </td>
            <td>
              {{ code.usageLimit }}
            </td>
            <td>
              {{ code.requireLogin }}
            </td>
          </tr>
        </tbody>
      </table>
    </pn-table>
    <pn-button v-on:click="close">Close</pn-button>
  </div>
</template>

<style lang="scss" scoped>
.date-picker {
  width: 55rem;
}
</style>
